import { createSlice } from "@reduxjs/toolkit";
import {
  addCourse,
  deleteCourse,
  fetchBranches,
  fetchCourses,
  fetchAdmissions,
  updateCourse,
  fetchStudentDocuments,
  fetchUserProfiles,
  deleteBranch,
  updateBranch,
  addBranch,
} from "../utils/Apis";

const dataSlice = createSlice({
  name: "data",
  initialState: {
    courses: [],
    branches: [],
    students: [],
    staffs: [],
    admissions: [],
    documents: [],
    adminUsers: [],
    loading: false,
    dataError: null,
  },
  reducers: {
    updateCourseStates: (state, action) => {
      const updatedCourse = action.payload;
      const index = state.courses.courses.findIndex(
        (course) => course._id === updatedCourse._id
      );

      if (index !== -1) {
        state.courses.courses[index] = {
          ...state.courses.courses[index],
          ...updatedCourse,
        };
      }
    },
    addCourseStates: (state, action) => {
      const newCourse = action.payload;
      state.courses.courses.push(newCourse);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourses.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.loading = false;
        state.courses = action.payload; // Action payload contains the data
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.loading = false;
        // Handle error from the payload if rejected
        state.dataError = action.payload || action.dataError.message;
      })
      .addCase(fetchBranches.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBranches.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = action.payload; // Action payload contains the data
      })
      .addCase(fetchBranches.rejected, (state, action) => {
        state.loading = false;
        // Handle dataError from the payload if rejected
        state.dataError = action.payload || action.dataError.message;
      })
      // Add Course extra reducer
      .addCase(addCourse.pending, (state) => {
        state.loading = true;
      })
      .addCase(addCourse.fulfilled, (state, action) => {
        state.loading = false;
        state.courses.courses.push(action.payload); // Add new course to list
      })
      .addCase(addCourse.rejected, (state, action) => {
        state.loading = false;
        state.dataError = action.payload || action.error.message;
      })
      // Update Course extra reducer
      .addCase(updateCourse.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCourse.fulfilled, (state, action) => {
        state.loading = false;
        const updatedCourse = action.payload;
        const index = state.courses.courses.findIndex(
          (course) => course._id === updatedCourse._id
        );

        if (index !== -1) {
          state.courses.courses[index] = {
            ...state.courses.courses[index],
            ...updatedCourse,
          };
        }
      })
      .addCase(updateCourse.rejected, (state, action) => {
        state.loading = false;
        state.dataError = action.payload || action.error.message;
      })
      // Delete course extra reducer
      .addCase(deleteCourse.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCourse.fulfilled, (state, action) => {
        state.loading = false;
        state.courses.courses = state.courses.courses.filter(
          (course) => course._id !== action.payload
        );
      })
      .addCase(deleteCourse.rejected, (state, action) => {
        state.loading = false;
        state.dataError = action.payload || action.error.message;
      })
      // add branch extra reducer
      .addCase(addBranch.pending, (state) => {
        state.loading = true;
      })
      .addCase(addBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.branches.branches.push(action.payload); // Add new branch to list
      })
      .addCase(addBranch.rejected, (state, action) => {
        state.loading = false;
        state.dataError = action.payload || action.error.message;
      })
      // update branch extra reducers
      .addCase(updateBranch.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBranch.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBranch = action.payload;
        const index = state.branches.branches.findIndex(
          (branch) => branch.id === updatedBranch._id
        );

        if (index !== -1) {
          state.branches.branches[index] = {
            ...state.branches.branches[index],
            ...updatedBranch,
          };
        }
      })
      .addCase(updateBranch.rejected, (state, action) => {
        state.loading = false;
        state.dataError = action.payload || action.error.message;
      })
      // delete branch extra reducer
      .addCase(deleteBranch.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteBranch.fulfilled, (state, action) => {
        state.loading = false;
        // Assuming the API returns the deleted branch object in action.payload.deletedBranch
        const deletedBranchId = action.payload.deletedBranch._id;
        state.branches.branches = state.branches.branches.filter(
          (branch) => branch.id !== deletedBranchId
        );
      })
      .addCase(deleteBranch.rejected, (state, action) => {
        state.loading = false;
        state.dataError = action.payload || action.error.message;
      })
      // fetch admissions extra reducers
      .addCase(fetchAdmissions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdmissions.fulfilled, (state, action) => {
        state.loading = false;
        state.admissions.admissions = state.admissions.admissions.filter(
          (admission) => admission._id !== action.payload
        );
      })
      .addCase(fetchAdmissions.rejected, (state, action) => {
        state.loading = false;
        state.dataError = action.payload || action.error.message;
      })
      // fetch student documents extra reducer
      .addCase(fetchStudentDocuments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStudentDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload.documents;
      })
      .addCase(fetchStudentDocuments.rejected, (state, action) => {
        state.loading = false;
        state.dataError = action.payload || "Failed to fetch documents.";
      })
      // fetch user profiles extra reducer (for admin profiles only)
      .addCase(fetchUserProfiles.pending, (state) => {
        state.loading = true; // Set loading to true when the request starts
      })
      .addCase(fetchUserProfiles.fulfilled, (state, action) => {
        state.loading = false; // Set loading to false when the request completes
        state.adminUsers = action.payload; // Store the fetched admin user data
      })
      .addCase(fetchUserProfiles.rejected, (state, action) => {
        state.loading = false; // Set loading to false if the request fails
        state.dataError = action.payload || action.error.message; // Handle error
      });
  },
});

export const { updatedCourseStates, addCourseStates } = dataSlice.actions;
export default dataSlice.reducer;
